// 💰 A MATEMÁTICA DO GRÁFICO DE FINANÇAS — fora do componente, de propósito.
//
// ═══ POR QUE ESTE ARQUIVO EXISTE (31/10/2026) ═══
//
// O balanço acumulado era montado dentro do `useMemo` da tela, com uma variável
// `let` somando dentro do `map`. As regras do compilador do React recusavam o
// trecho inteiro (`Cannot reassign variable after render completes`), e a recusa
// não ficava sozinha: quando o compilador desiste de um componente, ele também
// deixa de preservar a memoização manual do resto — os `useMemo` do `summary` e
// do `balance` caíam junto.
//
// A dívida ficou escrita no código em 31/10 com um motivo que continua valendo:
// **reescrever a acumulação às cegas troca um aviso de linter por um erro de
// saldo, e saldo errado numa tela de finanças não se percebe olhando.**
//
// 🔑 Por isso o conserto NÃO é uma reescrita: a conta saiu da tela e veio para
// cá **linha por linha, igual**. O que mudou de lugar foi o `let` — dentro de
// uma função de módulo ele é detalhe interno; dentro do render, é estado que o
// React não controla.
//
// 🧪 Provado por `scripts/conferir-balanco.mjs`, que roda a implementação velha
// (transcrita do commit anterior) e esta, lado a lado, sobre casos gerados
// incluindo as bordas que a tela produz de verdade — mês sem receita medida,
// aporte nulo, custo maior que entrada, lista vazia. Qualquer centavo de
// diferença derruba o script.
//
// ⚠️ QUEM MEXER AQUI mexe em dinheiro: aporte entra no BALANÇO e não na linha
// de receita, e a receita é a **efetiva** (a que o servidor usou), nunca
// recalculada no cliente. As duas escolhas estão comentadas abaixo, no ponto.

/**
 * O recorte de `FinanceRow` que a série usa. Estrutural de propósito: a série
 * não precisa conhecer as outras 25 colunas, e assim ela é testável com um
 * objeto de quatro campos.
 */
export interface MesDoBalanco {
  month:                        string;   // YYYY-MM-DD (primeiro dia)
  total_cost_cents:             number;
  cash_balance_projected_cents: number;
  revenue_cents:                number;
  revenue_effective_cents?:     number | null;
  capital_in_cents?:            number | null;
}

export interface PontoDaSerie {
  month:   string;   // rótulo curto, ex. "out/26"
  caixa:   number;
  custo:   number;
  receita: number;
  aporte:  number;
  balanco: number;   // acumulado desde o primeiro mês da lista
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** `2026-10-01` → `out/26`. */
export const rotuloCurtoDoMes = (iso: string) => {
  const [y, m] = iso.split('-');
  return `${MESES[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

/**
 * Monta a série do gráfico a partir das linhas da API.
 *
 * @param linhas vêm em ordem DESC (mês mais recente primeiro), como a rota
 *               `/admin/finances` entrega. A série sai ASC, que é a ordem em
 *               que uma trajetória se lê.
 */
export function construirSerieDoBalanco(linhas: MesDoBalanco[]): PontoDaSerie[] {
  const crescente = [...linhas].reverse();
  const serie: PontoDaSerie[] = [];
  let acumulado = 0;

  for (const r of crescente) {
    // `revenue_effective_cents` é o que o servidor usou de verdade: o medido
    // quando existe, o derivado quando não. Recalcular a escolha aqui abriria
    // espaço para o gráfico e a projeção de caixa discordarem em silêncio.
    const receita = r.revenue_effective_cents ?? r.revenue_cents ?? 0;
    const aporte  = r.capital_in_cents ?? 0;
    // Aporte entra no BALANÇO (é dinheiro que chegou) mas não na linha de
    // receita — senão a curva de receita passaria a ostentar dinheiro do
    // próprio Pablo como se fosse faturamento.
    acumulado += receita + aporte - r.total_cost_cents;
    serie.push({
      month:   rotuloCurtoDoMes(r.month.slice(0, 10)),
      caixa:   r.cash_balance_projected_cents / 100,
      custo:   r.total_cost_cents / 100,
      receita: receita / 100,
      aporte:  aporte  / 100,
      balanco: acumulado / 100,
    });
  }
  return serie;
}
